import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { visibleStaffNav, activeStaffKey } from './staffNav';
import StaffMailButton from './StaffMailButton';

const EDGE_GUTTER = 8;

function NavDropdown({ item, isActive, isOpen, onToggle, onClose }) {
    const location = useLocation();
    const panelRef = useRef(null);
    const shiftRef = useRef(0);
    const [shift, setShift] = useState(0);

    useLayoutEffect(() => {
        if (!isOpen) {
            shiftRef.current = 0;
            setShift(0);
            return undefined;
        }

        const fit = () => {
            const panel = panelRef.current;
            if (!panel) return;

            const rect = panel.getBoundingClientRect();
            const viewport = document.documentElement.clientWidth;
            const left = rect.left - shiftRef.current;
            const overflow = (left + rect.width) - (viewport - EDGE_GUTTER);
            const next = overflow > 0
                ? -Math.min(overflow, Math.max(0, left - EDGE_GUTTER))
                : 0;

            if (next !== shiftRef.current) {
                shiftRef.current = next;
                setShift(next);
            }
        };

        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, [isOpen]);

    return (
        <div className="relative">
            <button
                type="button"
                onClick={onToggle}
                className={`flex h-full items-center gap-1 px-3 py-2 font-['Jersey_10'] text-[19px] uppercase leading-none tracking-[1px] transition-colors
                    ${isActive ? 'bg-black/35 text-gold' : 'text-white hover:bg-black/20'}`}
                style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.5)' }}
            >
                {item.label}
                <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {isOpen && (
                <div
                    ref={panelRef}
                    className={`absolute left-0 top-full z-40 border border-black/40 bg-coal_dark ${item.wide ? 'w-[720px] max-w-[92vw]' : 'w-[260px]'}`}
                    style={{
                        boxShadow: '0 4px 0 0 rgba(0,0,0,0.4)',
                        transform: shift ? `translateX(${shift}px)` : undefined
                    }}
                >
                    <div className={item.wide ? 'grid grid-cols-2 gap-x-3 p-2 md:grid-cols-3' : 'flex flex-col p-2'}>
                        {item.groups.map((group, groupIndex) => (
                            <div key={groupIndex} className="mb-2 flex flex-col last:mb-0">
                                {group.header && (
                                    <span className="px-2 pb-1 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gold/70">
                                        {group.header}
                                    </span>
                                )}
                                {group.links.map(link => {
                                    const linkActive = location.pathname === link.path;
                                    return (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={onClose}
                                            className={`px-2 py-[6px] font-['Jersey_10'] text-[17px] uppercase leading-none tracking-[1px] transition-colors
                                                ${linkActive ? 'bg-maroon text-white' : 'text-white/85 hover:bg-black/40 hover:text-gold'}`}
                                        >
                                            {link.label}
                                        </Link>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function StaffPanelNav({ userData }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [openKey, setOpenKey] = useState('');
    const [mobileOpen, setMobileOpen] = useState(false);
    const navRef = useRef(null);

    const items = useMemo(() => visibleStaffNav(userData), [userData]);
    const activeKey = activeStaffKey(location.pathname);

    useEffect(() => {
        setOpenKey('');
        setMobileOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!openKey) return;
        const handler = (e) => {
            if (navRef.current && !navRef.current.contains(e.target)) setOpenKey('');
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [openKey]);

    return (
        <div
            ref={navRef}
            className="sticky top-0 z-40 flex max-h-screen w-full flex-col border-b border-black/40 bg-coal"
            style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.25)', maxHeight: '100dvh' }}
        >
            <div className="mx-auto flex w-full max-w-[1400px] shrink-0 items-stretch px-2">
                <button
                    type="button"
                    onClick={() => setMobileOpen(!mobileOpen)}
                    className="flex items-center gap-2 px-3 py-2 font-['Jersey_10'] text-[19px] uppercase tracking-[1px] text-white lg:hidden"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <line x1="3" y1="12" x2="21" y2="12" />
                        <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                    Menu
                </button>

                <div className="hidden items-stretch lg:flex">
                    {items.map(item => {
                        const isActive = activeKey === item.key;

                        if (item.path) {
                            return (
                                <Link
                                    key={item.key}
                                    to={item.path}
                                    className={`flex items-center px-3 py-2 font-['Jersey_10'] text-[19px] uppercase leading-none tracking-[1px] transition-colors
                                        ${isActive ? 'bg-black/35 text-gold' : item.gold ? 'text-[#d4af37] hover:bg-black/20' : 'text-white hover:bg-black/20'}`}
                                    style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.5)' }}
                                >
                                    {item.label}
                                </Link>
                            );
                        }

                        return (
                            <NavDropdown
                                key={item.key}
                                item={item}
                                isActive={isActive}
                                isOpen={openKey === item.key}
                                onToggle={() => setOpenKey(openKey === item.key ? '' : item.key)}
                                onClose={() => setOpenKey('')}
                            />
                        );
                    })}
                </div>

                <StaffMailButton />
            </div>

            {mobileOpen && (
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-black/40 bg-coal_dark lg:hidden">
                    {items.map(item => (
                        <div key={item.key} className="border-b border-black/30">
                            {item.path ? (
                                <button
                                    type="button"
                                    onClick={() => navigate(item.path)}
                                    className="w-full px-4 py-2 text-left font-['Jersey_10'] text-[19px] uppercase tracking-[1px] text-white"
                                >
                                    {item.label}
                                </button>
                            ) : (
                                <div className="py-1">
                                    <span className="block px-4 py-1 font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-gold/70">
                                        {item.label}
                                    </span>
                                    {item.groups.map((group, groupIndex) => (
                                        <div key={groupIndex}>
                                            {group.links.map(link => (
                                                <Link
                                                    key={link.path}
                                                    to={link.path}
                                                    className="block px-6 py-[6px] font-['Jersey_10'] text-[17px] uppercase tracking-[1px] text-white/85"
                                                >
                                                    {link.label}
                                                </Link>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default StaffPanelNav;
